import React, { useState, useEffect, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
    Alert,
    Col,
    Container,
    Row,
    TopBar,
    NumberInput,
    Modal
} from 'components'
import { ContinueButton } from 'components/button/ContinueButton'
import { useTranslation } from 'react-i18next'
import { useValidation, useBindDispatch, useAppSelector } from 'hooks'
import {
    constants,
    getConnectUser,
    VillageType,
    validationRules,
    sendMessageToWorkerWithResponse,
    getStorage,
    permissionFlags,
    selectClientFromPhoneMatches,
    openClientFromSearch,
    resolveEnrollmentPath
} from 'helpers'
import { findPermission } from 'helpers/findPermission'
import { FarmerIdModal } from 'pages/farmerId/FarmerIdModal'
import { ClientType, ClientSearchEnum } from 'storeTypes'
import './clientSearch.scss'

const countryPhoneLengthConfig: Record<string, number> = {
    Mali: 8,
    Senegal: 9,
    "Cote D'Ivoire": 10
}

export const ClientSearch = () => {
    const [animate, setAnimate] = useState(false)
    const [animationList, setAnimationList] = useState(false)
    const [phone, setPhone] = useState('')
    const [triggerSearch, setTriggerSearch] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [showFarmerIdModal, setShowFarmerIdModal] = useState(false)
    const [selectedClient, setSelectedClient] = useState<ClientType>()
    const nodeRef = useRef(null)
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const villageIdFromQuery = searchParams.get('villageId') || undefined
    const villageReturnPath = villageIdFromQuery
        ? `/village/${encodeURIComponent(villageIdFromQuery)}`
        : undefined

    const {
        clientSearchByPhoneHandler,
        clearClientSearchHandler,
        villageSelectionInfoHandler,
        selectedClientHandler,
        getClientOrdersHandler
    } = useBindDispatch()

    const { searchResult } = useAppSelector((state) => state.clientSearch)
    const { clientOrders } = useAppSelector((state) => state.clientOrders)
    const { country, veVillages } = getConnectUser()
    const villageIds = villageIdFromQuery
        ? [villageIdFromQuery]
        : veVillages?.map((village: VillageType) => village.sf_id) || []

    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const canUseFarmerId = findPermission(
        userPermissions,
        permissionFlags.canUseFarmerId
    )

    const phoneNumberValidationRule = {
        phone: validationRules({
            country,
            phone
        }).phone
    }
    const { errors, validateForm, resetError } = useValidation(
        { phone },
        phoneNumberValidationRule
    )

    useEffect(() => {
        document.title = t('clientSearch')
        window.scrollTo(0, 0)
        localStorage.removeItem(constants.CLIENT_FOR_ENROLLMENT_STORE)
        setAnimate(true)
    }, [])

    useEffect(() => {
        if (phone.length > 0) {
            validateForm()
        }
    }, [phone, country])

    const openMatchedClient = (client: ClientType) => {
        openClientFromSearch(client, {
            canUseFarmerId,
            // ATC village entry: back from profile must not drop villageId
            // (pathname is /client-search without the query).
            pathname: villageReturnPath || pathname,
            navigate,
            getClientOrders: getClientOrdersHandler,
            onShowFarmerIdModal: (farmer: ClientType) => {
                setSelectedClient(farmer)
                setShowFarmerIdModal(true)
                setTriggerSearch(false)
            }
        })
    }

    const continueToEnrollment = async () => {
        try {
            const result = await resolveEnrollmentPath({
                veVillages: veVillages || [],
                pathname,
                villageId: villageIdFromQuery,
                getBlankIdCount: () =>
                    sendMessageToWorkerWithResponse({
                        'get blank id count': 'client needs a blank id'
                    })
            })

            if (result.kind === 'error') {
                setTimeout(() => setErrorMsg(''), 5000)
                setErrorMsg(t(result.messageKey))
                setTriggerSearch(false)
                return
            }

            if (result.kind === 'navigate') {
                navigate(result.path)
                return
            }

            villageSelectionInfoHandler({
                url: result.enrollBaseUrl
            })
            navigate('/village/select-village')
        } catch (error) {
            setErrorMsg(t(error as string))
            setTriggerSearch(false)
        }
    }

    useEffect(() => {
        if (!triggerSearch) return

        const handleSearchOutcome = async () => {
            const matchedClient = selectClientFromPhoneMatches(
                searchResult,
                phone
            )

            if (!matchedClient) {
                await continueToEnrollment()
                return
            }

            openMatchedClient(matchedClient)
        }

        handleSearchOutcome()
    }, [searchResult])

    const searchSubmitHandler = () => {
        resetError()
        if (!phone || !validateForm()) return

        if (villageIdFromQuery) {
            dispatch({
                type: ClientSearchEnum.SEARCH_CLIENT_BY_PHONE,
                payload: {
                    allClientList: [],
                    searchParams: { primaryPhone: phone }
                }
            })
            continueToEnrollment()
            return
        }

        clientSearchByPhoneHandler({
            phone,
            villageIds
        })
        setTriggerSearch(true)
    }

    const closeClientSearchPage = () => {
        clearClientSearchHandler()
        navigate(villageReturnPath || '/')
    }

    const phoneLength =
        countryPhoneLengthConfig[country || 'Senegal'] ||
        countryPhoneLengthConfig.Senegal

    const clientSearchPage: JSX.Element = (
        <CSSTransition
            unmountOnExit
            timeout={constants.ANIMATION_TIMEOUT}
            in={animate}
            classNames="generic"
            appear
            onEnter={() => setAnimationList(true)}
            nodeRef={nodeRef}
        >
            <div
                className="client-search--main-parent page-wrapper"
                ref={nodeRef}
            >
                <Container>
                    <Row>
                        <Col md={12}>
                            <div
                                className={
                                    animationList
                                        ? 'client-search--phone-form animate-show-4 animate-showed-4'
                                        : 'client-search--phone-form animate-show-4'
                                }
                            >
                                <h2 className="client-search--phone-title">
                                    {t('enrollment.primaryPhone')}
                                </h2>
                                <div className="client-search--phone-input">
                                    <NumberInput
                                        length={phoneLength}
                                        onChangeNumberHandler={setPhone}
                                        className={
                                            phoneLength > 7
                                                ? 'enrollment--input-number'
                                                : ''
                                        }
                                        initialValue={phone}
                                        name="primaryPhone"
                                    />
                                    {errors.phone && (
                                        <p
                                            className="client-search--phone-error"
                                            data-testid="client-search-error"
                                        >
                                            {errors.phone}
                                        </p>
                                    )}
                                </div>
                                {phone && !errors.phone && (
                                    <ContinueButton
                                        onClick={searchSubmitHandler}
                                        btnText={t('next')}
                                        className="client-search--next-button"
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </CSSTransition>
    )

    return (
        <>
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar back close={() => closeClientSearchPage()}>
                        {t('enrollment.enrollment')}
                    </TopBar>
                </Container>
            </div>
            <Modal
                showModal={showFarmerIdModal}
                data-testid="farmer-id-modal"
                position={'center'}
                className="farmer-id--modal"
                closeClickHandler={() => setShowFarmerIdModal(false)}
                showCloseIconBtn
            >
                {selectedClient && (
                    <FarmerIdModal
                        clientData={selectedClient}
                        selectClientHandler={selectedClientHandler}
                        closeModalHandler={() => setShowFarmerIdModal(false)}
                        clientOrders={clientOrders || []}
                        returnPath={villageReturnPath || pathname}
                    />
                )}
            </Modal>
            {clientSearchPage}
            {errorMsg && (
                <Alert type="floating" status={'error'}>
                    {errorMsg}
                </Alert>
            )}
        </>
    )
}
